/**
 * Document index API service.
 */

import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface DocIndex {
  id: string;
  repo_full_name: string;
  path: string;
  title: string;
  doc_type: string;
  scenario_count: number;
  size: number;
  synced_at: string;
  /** An ADR's two facts (design-pillar-reads-the-decisions): the number its
   *  file name carries and the state its header declares ('' when none the
   *  vocabulary knows). Absent on every other kind. */
  decision_number?: number | null;
  decision_status?: string;
  frontmatter?: Record<string, unknown>;
  snippet?: string;
}

/** How many documents of one repository the caller can read (GET /docs/coverage). */
export interface DocCoverage {
  repo_full_name: string;
  doc_count: number;
}

export interface TreeNode {
  name: string;
  path: string;
  type: 'file' | 'dir';
  doc_type?: string;
  title?: string;
  children: TreeNode[];
}

export interface DocContent {
  content: string;
  cached: boolean;
  /** The git blob sha of the loaded file — the base for optimistic concurrency
   *  on save (git-native-editing). Absent on error or for legacy responses. */
  sha?: string;
  error?: string;
}

/** Thrown by `saveFileContent` when the file changed in git since it was loaded
 *  (HTTP 409). The caller keeps its local buffer and surfaces the conflict
 *  rather than overwriting the newer version. */
export class DocConflictError extends Error {
  constructor(message = 'This document changed in GitHub since you opened it.') {
    super(message);
    this.name = 'DocConflictError';
  }
}

export interface DocSearchResult {
  items: DocIndex[];
  total: number;
  offset: number;
  limit: number;
}

export interface DashboardSummary {
  repos: { full_name: string; doc_count: number; sync_status: string; last_sync_at: string | null }[];
  total_docs: number;
  doc_types: Record<string, number>;
  recent_docs: DocIndex[];
}

export interface RepoSummary {
  has_summary: boolean;
  content: string | null;
}

export interface RepoBranches {
  branches: string[];
  default_branch: string;
}

export const docsApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /**
   * `view: 'summary'` reads the listing index (fast-document-browsing): the
   * fields a list shows and nothing else — no outline, no snippet, no `q`.
   * Every list in the app asks for it; the full view is for the MCP clients
   * that cite a document's sections.
   */
  async listDocs(params?: { repo?: string; type?: string; pathPrefix?: string; q?: string; limit?: number; offset?: number; view?: 'summary' }): Promise<DocIndex[]> {
    const { data } = await api.get('/docs', { params });
    // Handle both old (array) and new (object with items) response formats
    if (Array.isArray(data)) return data;
    return data.items || [];
  },

  async searchDocs(params: { q: string; repo?: string; type?: string; project?: string; limit?: number; offset?: number }): Promise<DocSearchResult> {
    const { data } = await api.get('/docs', { params });
    if (Array.isArray(data)) {
      return { items: data, total: data.length, offset: 0, limit: data.length };
    }
    return data;
  },

  /** Per-repository document counts — which repositories have documentation. */
  async getDocCoverage(): Promise<DocCoverage[]> {
    const { data } = await api.get('/docs/coverage');
    return data.repos || [];
  },

  async getDoc(docId: string): Promise<DocIndex> {
    // Use query parameter — doc IDs contain slashes which API Gateway can't handle in path segments
    const { data } = await api.get('/docs/detail', { params: { id: docId } });
    return data;
  },

  async getDocContent(docId: string): Promise<DocContent> {
    const { data } = await api.get('/docs/content', { params: { id: docId } });
    return data;
  },

  async getRepoTree(owner: string, repo: string): Promise<TreeNode> {
    const { data } = await api.get(`/repos/${owner}/${repo}/tree`);
    return data;
  },

  async getBranchTree(owner: string, repo: string, ref: string): Promise<TreeNode> {
    const { data } = await api.get(`/repos/${owner}/${repo}/tree/branch`, { params: { ref } });
    return data;
  },

  async getRepoSummary(owner: string, repo: string): Promise<RepoSummary> {
    const { data } = await api.get(`/repos/${owner}/${repo}/summary`);
    return data;
  },

  async getRepoBranches(owner: string, repo: string): Promise<RepoBranches> {
    const { data } = await api.get(`/repos/${owner}/${repo}/branches`);
    return data;
  },

  async getFileContent(owner: string, repo: string, path: string, ref?: string): Promise<DocContent> {
    const { data } = await api.get(`/repos/${owner}/${repo}/content`, { params: { path, ref } });
    return data;
  },

  async saveFileContent(
    owner: string,
    repo: string,
    path: string,
    content: string,
    branch?: string,
    /** The blob sha the editor loaded; when set the write is conditional and a
     *  concurrent change becomes a DocConflictError instead of a silent overwrite. */
    baseSha?: string,
  ): Promise<{ commitSha: string; contentSha: string; path: string }> {
    try {
      const { data } = await api.put(
        `/repos/${owner}/${repo}/content`, { path, content, branch, baseSha });
      return data;
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 409) {
        throw new DocConflictError();
      }
      throw e;
    }
  },

  async syncRepo(owner: string, repo: string): Promise<{ status: string; files_indexed: number }> {
    const { data } = await api.post(`/repos/${owner}/${repo}/sync`);
    return data;
  },

  async getSyncStatus(owner: string, repo: string): Promise<{ repo: string; status: string; last_sync_at: string | null; file_count: number }> {
    const { data } = await api.get(`/repos/${owner}/${repo}/sync/status`);
    return data;
  },

  /** Admin: get global SystemDox statistics. */
  async getAdminStats(): Promise<{ installations: number; documents: number; specs: number; repos: number }> {
    const { data } = await api.get('/admin/stats');
    return data;
  },

  /** Admin: list all GitHub App installations across tenants. */
  async getAdminInstallations(): Promise<{ tenant: string; installationId: number; accountLogin: string; accountType: string; connectedAt: string }[]> {
    const { data } = await api.get('/admin/installations');
    return data;
  },

};
